import { createBrowserRouter, RouterProvider, Navigate } from "react-router-dom";
import { AuthProvider } from "@marekl/adminer";
import { RequireAuth, RouteError } from "@marekl/adminer/shell";
import { RequireGuest } from "./auth/RequireGuest";
import { AppLayout } from "./shell/AppLayout";
import { LoginPage } from "./pages/LoginPage";
import { DashboardPage } from "./pages/DashboardPage";
import { MessagesPage, MessageDetailRouteModal } from "./pages/MessagesPage";
import { BatchesPage } from "./pages/BatchesPage";
import { TemplatesPage, TemplateDetailPage } from "./pages/TemplatesPage";
import { SendersPage } from "./pages/SendersPage";
import { SuppressionsPage } from "./pages/SuppressionsPage";
import { ApiKeysPage } from "./pages/ApiKeysPage";
import { WorkersPage } from "./pages/WorkersPage";
import { WebhooksPage } from "./pages/WebhooksPage";
import { AuditLogPage } from "./pages/AuditLogPage";
import { SettingsPage } from "./pages/SettingsPage";

/**
 * Data router (createBrowserRouter + RouterProvider). The app deliberately
 * moved off the declarative <BrowserRouter> so that
 *   - `useUnsavedChangesGuard` works for in-app navigation (useBlocker is a
 *     data-router API), and
 *   - route modals use the nesting form: /messages/:id is a child of
 *     /messages, so the list stays mounted under the dialog by construction.
 */
/**
 * The library's route error screen, in this app's words. It hangs on the root
 * routes, which replaces <AdminerProvider> with it — so the copy is passed
 * rather than read through the translator.
 */
function RouteErrorPage() {
  return (
    <RouteError
      homeTo="/dashboard"
      title="Something went wrong"
      subtitle="This page could not be shown. Try loading it again; if it keeps happening, tell us what you were doing."
      homeLabel="Back to overview"
      reloadLabel="Reload"
    />
  );
}

const router = createBrowserRouter([
  {
    path: "/login",
    element: (
      <RequireGuest>
        <LoginPage />
      </RequireGuest>
    ),
    errorElement: <RouteErrorPage />,
  },
  {
    element: (
      <RequireAuth>
        <AppLayout />
      </RequireAuth>
    ),
    // A data router catches render errors inside a route and looks for that
    // route's errorElement — the <ErrorBoundary> in main.tsx sits above
    // RouterProvider and never sees them. Without this, a crash on any page
    // is answered by React Router's built-in developer screen.
    errorElement: <RouteErrorPage />,
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      { path: "/dashboard", element: <DashboardPage /> },
      {
        path: "/messages",
        element: <MessagesPage />,
        children: [{ path: ":id", element: <MessageDetailRouteModal /> }],
      },
      { path: "/batches", element: <BatchesPage /> },
      { path: "/templates", element: <TemplatesPage /> },
      { path: "/templates/:id", element: <TemplateDetailPage /> },
      { path: "/senders", element: <SendersPage /> },
      { path: "/suppressions", element: <SuppressionsPage /> },
      { path: "/api-keys", element: <ApiKeysPage /> },
      { path: "/workers", element: <WorkersPage /> },
      { path: "/webhooks", element: <WebhooksPage /> },
      { path: "/audit-log", element: <AuditLogPage /> },
      { path: "/settings", element: <SettingsPage /> },
    ],
  },
  { path: "*", element: <Navigate to="/dashboard" replace /> },
]);

export default function App() {
  return (
    <AuthProvider>
      <RouterProvider router={router} />
    </AuthProvider>
  );
}
